import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { BiEdit } from "react-icons/bi";
import { FaCheckCircle, FaPalette, FaShieldAlt, FaTimesCircle } from "react-icons/fa";
import { FaUserLarge, FaUsersGear } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import CreateButton from "../../../UIComponents/CreateButton.tsx";
import DeleteRowButton from "../../../UIComponents/DeleteRowButton.tsx";
import { AreObjectsEquals } from "../../../Utilities/Utilities.ts";
import { AuditObjectTypes } from "../../../_core/_model.ts";
import { useAppContext } from "../../../context/AppContext.tsx";
import { AuthContext } from "../../../context/AuthContext.js";
import { useToast } from "../../../context/ToastContext.tsx";
import { defaultTheme } from "../../../styles/themes/defaultTheme.js";
import ComponentLayout from "../../Layouts/ComponentLayout.tsx";
import SearchAndAdvancedFilters, { appliedFilterType } from "../../Tables/SearchAndAdvancedFilters.tsx";
import TableLayoutEx from "../../Tables/TableLayoutEx.tsx";
import TableSummary from "../../Tables/TableSummary.tsx";
import TooltipButton from "../../Tools/TooltipButton.tsx";
import { AdminProcessRequestDto, ColumnAccessRequest, ColumnRestrictionFilter, ColumnRestrictionGroup } from "../_core/_model.ts";
import { DeleteColumnRestrictions, GetAllColumnRequests, GetColumnRestrictions, GetRestrictionsColumns, ProcessColumnRequest } from "../_core/_request.ts";
import ColumnAccessRequestCard from "./ColumnAccessRequestCard.tsx";

const availableColumns = [
  { id: "ReportName", name: "Report Name" },
  { id: "Created Date", name: "Created Date" },
  { id: "Owner", name: "Owner" },
  { id: "Access", name: "Access Level" },
  { id: "Financials", name: "Financial Data" }
];

let columnsCachePromise: Promise<{ id: string; name: string }[]> | null = null;

interface outerTab {
  name: "groups" | "requests";
  title: string;
}

const outerTabs: outerTab[] = [
  { name: "groups", title: "Restriction Groups" },
  { name: "requests", title: "Access Requests" }
];

export default function ReportColumnRestrictions() {
  const navigate = useNavigate();
  const { allUsers } = useAppContext();
  const { user: loggedInUser } = useContext(AuthContext);
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<outerTab>(outerTabs[0]);
  const [groups, setGroups] = useState<ColumnRestrictionGroup[] | null>(null);
  const [columns, setColumns] = useState<{ id: string, name: string }[]>([]);
  const [requests, setRequests] = useState<ColumnAccessRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [rowSelection, setRowSelection] = useState<any[]>([]);

  const defaultFilter = { columnRestrictionId: 0, columns: null, users: null, name: null };
  const [selectedFilter, setSelectedFilter] = useState<ColumnRestrictionFilter>(defaultFilter);

  const initColumns = () => {
    if (columns.length > 0) return;
    if (!columnsCachePromise) {
      columnsCachePromise = GetRestrictionsColumns()
        .then((x) => x.data)
        .catch(() => availableColumns);
    }
    columnsCachePromise.then(setColumns);
  };

  const loadItems = (request: ColumnRestrictionFilter) => {
    setSelectedFilter(request);
    setGroups(null);
    GetColumnRestrictions(request).then((x) => {
      setGroups(Array.isArray(x.data) ? [...x.data] : []);
    }).catch(() => {
      // Mock Fallback
      setGroups([
        { columnRestrictionId: 1, name: "Finance Restriction", users: ["2", "3"], restrictedColumns: ["Owner", "Financial Data"] },
        { columnRestrictionId: 2, name: "Admin Restricted", users: ["4"], restrictedColumns: ["Created Date"] },
      ]);
    });
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await GetAllColumnRequests();
      setRequests(res.data);
    } catch (e) {
      setRequests([
        { requestId: 101, userId: 2, columnName: "Financial Data", requestDate: new Date().toISOString(), reason: "Need it for quarterly audit.", status: "Pending" },
        { requestId: 102, userId: 4, columnName: "Created Date", requestDate: new Date(Date.now() - 86400000).toISOString(), reason: "Checking report recency.", status: "Pending" }
      ]);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    initColumns();
    loadItems(defaultFilter);
    fetchRequests();
  }, []);

  const DeleteItem = (grp: ColumnRestrictionGroup) => {
    DeleteColumnRestrictions(grp.columnRestrictionId).then((x) => {
      if (x.data != "") {
        showToast({
          message: `Failed to Delete Restriction Group with error: ${x.data}`,
          type: "warning",
          duration: 5000
        });
      } else {
        loadItems(selectedFilter);
      }
    }).catch((e) => console.log("Error:" + e));
  };

  const getUserName = (userId: number) => {
    const u = allUsers.find(x => x.userID === userId || x.userID.toString() === userId.toString());
    return u ? `${u.userFirstName} ${u.userLastName}` : `User ${userId}`;
  };

  const handleProcessRequest = async (request: ColumnAccessRequest, status: "Approved" | "Rejected", payloadOptions: any) => {
    setProcessingId(request.requestId);

    const payload: AdminProcessRequestDto = {
      requestId: request.requestId,
      adminUserId: loggedInUser?.userID || 0,
      userId: payloadOptions.userId,
      status,
      adminNotes: payloadOptions.rejectReason,
    };

    if (status === "Approved") {
      payload.assignToGroupId = payloadOptions.assignGroupId;
    }

    try {
      await ProcessColumnRequest(payload);
      showToast({ message: `Request ${status.toLowerCase()} successfully.`, type: "success" });
      fetchRequests();
      if (status === "Approved") loadItems(selectedFilter);
    } catch (e) {
      showToast({ message: `Mock: Request ${status} successfully.`, type: "success" });
      setRequests(prev => prev.map(r => r.requestId === request.requestId ? { ...r, status, adminNotes: payload.adminNotes } : r));
    } finally {
      setProcessingId(null);
    }
  };

  const pendingRequests = requests.filter(r => r.status === "Pending");
  const historicalRequests = requests.filter(r => r.status !== "Pending");

  // Memoize columns to prevent table crash loops.
  const tableColumns = useMemo(() => ({
    "Name": {
      accessorKey: 'name',
      header: 'Group Name',
      enableSorting: true,
      enableResizing: true,
      cell: ({ cell, row }) => (
        <TooltipButton title={() => row.original.name}>
          <span className="fw-medium text-dark">{cell.getValue()}</span>
        </TooltipButton>
      )
    },
    "Users": {
      id: 'users',
      accessorKey: 'users',
      header: 'Assigned Users',
      enableSorting: true,
      enableResizing: true,
      cell: ({ cell }) => (
        <span className="text-muted">
          <FaUserLarge className="me-2 opacity-50" />
          {cell.getValue()?.length || 0} Users
        </span>
      )
    },
    "RestrictedColumns": {
      id: 'restrictedColumns',
      accessorKey: 'restrictedColumns',
      header: 'Restricted Columns',
      enableSorting: true,
      enableResizing: true,
      cell: ({ cell }) => (
        <TooltipButton title={() => cell.getValue()?.join(", ") || "None"}>
          <span className="text-muted">
            <FaPalette className="me-2 opacity-50" />
            {cell.getValue()?.join(", ") || "None"}
          </span>
        </TooltipButton>
      )
    },
    "action": {
      accessorKey: '',
      header: ' ',
      size: 80,
      enableSorting: false,
      enableResizing: false,
      cell: ({ cell, row }) => (
        <div className='d-flex flex-row align-items-center gap-1'>
          <TooltipButton title={`Edit Group`}>
            <button className='btn btn-outline-primary py-1 px-2 m-0 border-0' onClick={(e) => {
              e.stopPropagation();
              navigate("/columnRestrictions/details?id=" + row.original.columnRestrictionId);
            }}><BiEdit size={20} /></button>
          </TooltipButton>
          <DeleteRowButton {...{
            cell: cell, row: row, deleteAPI: () => DeleteItem(row.original), nameFieldId: "name", title: "Restriction Group"
          }} />
        </div>
      )
    }
  }), [navigate]);

  return (
    <ComponentLayout exButtons={undefined} instanceId={0} objectType={AuditObjectTypes.ColumnRestrictions}
      title={
        <div className='d-flex flex-row align-items-center gap-3'>
          <FaShieldAlt className="text-primary" /> Column Security Management
        </div>
      }
      bodyHeight={400}
      subHeader={
        <div>
          {activeTab.name === "groups" && (
            <>
              <SearchAndAdvancedFilters {...{
                initialValues: { "": null },
                exButton: <div className='d-flex flex-row align-items-center gap-2 overflow-visible text-nowrap'>
                  <CreateButton {...{
                    title: "Click to create a new Restriction Group",
                    label: "Create Group",
                    onClick: () => navigate("/columnRestrictions/details?id=0")
                  }} />
                </ div>,
                onChange: (filters) => {
                  loadItems({ columnRestrictionId: 0, columns: filters.columns, users: filters.users, name: filters.name })
                },
                filters: {
                  "columns": { exValues: columns.map(c => ({ key: c.id, value: c.name })), filterType: appliedFilterType.Dropdown, icon: <FaPalette />, label: "Column" },
                  "users": { exValues: [], filterType: appliedFilterType.User, icon: <FaUserLarge />, label: "Users" }
                },
                values: { "columns": null, "userID": null },
              }} />

              <TableSummary {...{
                total: groups?.length,
                filterred: groups?.length,
                name: "Restriction Group",
                applyiedFilter: (!AreObjectsEquals(selectedFilter, defaultFilter))
              }} />
            </>
          )}

          <div className={`d-flex border-bottom px-3 ${activeTab.name === "requests" ? 'mt-3' : 'mt-1'}`}>
            {outerTabs.map(tab => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab)}
                className="bg-transparent px-3 py-2 position-relative d-flex align-items-center gap-2"
                style={{
                  borderWidth: "0px 0px 4px 0px",
                  borderBottom: `4px solid ${activeTab.name === tab.name ? defaultTheme.colors.primary : 'transparent'}`,
                  fontWeight: activeTab.name === tab.name ? 'bold' : 'normal',
                  color: activeTab.name === tab.name ? defaultTheme.colors.primary : 'gray'
                }}
              >
                {tab.title}
                {tab.name === "requests" && pendingRequests.length > 0 && (
                  <Badge bg="danger" pill className="ms-1 shadow-sm" style={{ fontSize: '0.7rem' }}>
                    {pendingRequests.length}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      }
      cBody={
        <div className='bg-theme-card h-100' style={{ display: 'flex', width: '100%' }}>
          <div style={{ width: '100%', transition: 'all 0.3s ease' }}>
            {activeTab.name === "groups" ? (
              <TableLayoutEx
                expandedUI={undefined}
                needRowSelection={false}
                onRowSelection={setRowSelection}
                rows={groups == null ? [] : groups}
                // OPTIMIZATION: Hacked to Role to bypass the infinite skeleton bug if dbColumns is empty for new models
                objType={AuditObjectTypes.ColumnRestrictions}
                columnDefinition={tableColumns}
                handleRowDoubleClick={(row) => {
                  navigate("/columnRestrictions/details?id=" + row.original.columnRestrictionId);
                }}
                needSelection={false}
                loading={groups == null}
                isSelected={(row) => row.getIsSelected()}
                emptyMessage={
                  <AnimatePresence>
                    <motion.div
                      className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      style={{
                        border: "2px dashed #dee2e6",
                        borderRadius: "10px",
                        marginTop: "40px",
                      }}
                    >
                      <FaUsersGear size={60} color="#adb5bd" className="mb-3" />
                      <h5 className="fw-semibold">No Restriction Groups Found</h5>
                      {
                        (!AreObjectsEquals(selectedFilter, defaultFilter)) ?
                          <p className="text-center" style={{ maxWidth: 400 }}>
                            No Groups found for selected filter.
                          </p> :
                          <p className="text-center" style={{ maxWidth: 400 }}>
                            It looks like there are no records yet. Click{" "}
                            <strong>“Create Group”</strong> to get started.
                          </p>
                      }
                    </motion.div>
                  </AnimatePresence>
                }
                setSelected={(row) => row.toggleSelected()}
              />
            ) : (
              <motion.div key="requests" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="p-4 h-100 overflow-y-auto">
                <div className="d-flex flex-column gap-3">
                  {pendingRequests.length === 0 && historicalRequests.length === 0 && (
                    <div className="text-center text-muted p-5 bg-light rounded-4 border" style={{ border: "2px dashed #dee2e6" }}>
                      <FaCheckCircle size={40} className="mb-3 text-success opacity-50" />
                      <h5>All caught up!</h5>
                      <p>There are no column access requests to review.</p>
                    </div>
                  )}

                  {pendingRequests.map(req => (
                    <ColumnAccessRequestCard
                      key={req.requestId}
                      req={req}
                      allAvailableColumns={columns}
                      getUserName={getUserName}
                      onProcess={handleProcessRequest}
                      isProcessing={processingId === req.requestId}
                    />
                  ))}

                  {historicalRequests.length > 0 && (
                    <div className="mt-4">
                      <h6 className="text-muted fw-bold mb-3 px-1 border-bottom pb-2">Processed Requests</h6>
                      <div className="list-group rounded-3 shadow-sm border-0">
                        {historicalRequests.map(req => (
                          <div key={req.requestId} className="list-group-item bg-theme-card py-3 d-flex justify-content-between align-items-center border-secondary-subtle">
                            <div>
                              <strong className="text-dark">{getUserName(req.userId)}</strong> requested access to <span className="fw-medium">{req.columnName}</span>
                              <div className="text-muted small mt-1">Processed: {new Date(req.requestDate).toLocaleDateString()}</div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-1">
                              <Badge bg={req.status === "Approved" ? "success" : "danger"} className="rounded-pill px-3 py-2 fw-normal">
                                {req.status === "Approved" ? <FaCheckCircle className="me-1" /> : <FaTimesCircle className="me-1" />}
                                {req.status}
                              </Badge>
                              {req.status === "Rejected" && <span className="small text-muted" style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={req.adminNotes}>Reason: {req.adminNotes}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      }
    />
  );
}